import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import {
	AgendaLayoutTypesBold,
	AgendaLayoutTypesClassic,
	AgendaLayoutTypesFun,
	TemplateClassNames,
} from '../../../../types/template-layouts';
import { LanguagesAbbr, PageModule, SessionPreview } from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
import { EIcon } from '../../../general-ui/icon/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../utils';
import SessionCard from './agenda-session-card';
import { filterSessions, SessionFilter, SessionFilterArgs } from './session-filter';
interface OnDemandSessionsProps {
	module: PageModule;
	template: TemplateClassNames;
	sessions: SessionPreview[];
	isLive: boolean;
}
export const OnDemandSessions = ({
	module,
	template,
	sessions,
	isLive,
}: OnDemandSessionsProps): JSX.Element => {
	const { content, styling_overrides } = module;
	const layout_type = content?.layout_type || AgendaLayoutTypesClassic.standard;
	const { t } = useTranslation("homepage");
	const isFourSessions = useMemo(
		() =>
			layout_type === AgendaLayoutTypesClassic.contained_card ||
			layout_type === AgendaLayoutTypesClassic.contained_card_no_image ||
			layout_type === AgendaLayoutTypesBold.contained_card ||
			layout_type === AgendaLayoutTypesFun.contained_card,
		[layout_type]
	);

	const step = useMemo(() => (isFourSessions ? 2 : 3), [isFourSessions]);
	const [size, setSize] = useState(isFourSessions ? 4 : 3);

	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		dropdownOverrides,
	} = stylingOverrides;

	const [filter, setFilter] = useState<SessionFilterArgs>();

	const filteredSessions = useMemo<SessionPreview[]>(
		() => filterSessions({ sessions, filter }),
		[sessions, filter]
	);
	return (
		<div
			className={classNames(
				'agenda-ondemand-sessions-container agenda-sessions-wrapper'
			)}
		>
			<div
				className={classNames(
					'agenda-ondemand-sessions-header agenda-sessions-header'
				)}
			>
				{!isLive &&
					<TypographyItem
						className={classNames('agenda-sessions-header-title', {
							'evt-heading-3': template === TemplateClassNames.Apollo,
							'evt-heading-4': template !== TemplateClassNames.Apollo,
						})}
						tagName="h3"
					>
						{t('On demand sessions')}
					</TypographyItem>
				}

				<div
					className={classNames(
						'agenda-filters',
						dropdownOverrides
					)}
				>
					<SessionFilter
						isLive={isLive}
						module={module}
						sessions={sessions}
						template={template}
						onFilterChange={setFilter}
					/>
				</div>
			</div>
			<div
				className={classNames('agenda-ondemand-sessions agenda-sessions-list')}
			>
				{filteredSessions.slice(0, size).map((session: SessionPreview, i: number) => {
					return (
						<SessionCard
							key={session.uuid}
							selectedLanguage={filter?.language as LanguagesAbbr}
							session={session}
							stylingOverrides={stylingOverrides}
							template={template}
							defaultImage={getDefaultAgendaImage(template, i)}
						/>
					);
				})}
			</div>
			<div className="agenda-show-more-wrapper">
				{filteredSessions.length > size && (
					<Button onClick={() => setSize(size + step)}
						classButton="secondary"
						template={template}
					>
						{template === TemplateClassNames.Apollo && (
							<EIcon
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
						)}
						{t("Show more")}
					</Button>
				)}
			</div>
		</div>
	);
};
